import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Copy, Download, Save, Trash2, Calendar, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SocialPanel() {
  const [form, setForm] = useState({ topic: "", service: "general", platform: "Instagram", tone: "elegante, cercano y profesional", include_image: true, palette: "latte_coral" });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const [scheduleFor, setScheduleFor] = useState({});
  const [metaConfigured, setMetaConfigured] = useState(false);
  const [palettes, setPalettes] = useState([]);

  const loadSaved = async () => {
    try { const r = await api.get("/social-posts"); setSaved(r.data); } catch (_) { /* noop */ }
  };
  const loadMeta = async () => {
    try { const r = await api.get("/meta/status"); setMetaConfigured(r.data.configured && r.data.token_check?.ok); } catch (_) { /* noop */ }
  };
  const loadPalettes = async () => {
    try { const r = await api.get("/brand/palettes"); setPalettes(r.data.palettes || []); } catch (_) { /* noop */ }
  };
  useEffect(() => { loadSaved(); loadMeta(); loadPalettes(); }, []);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Escribe un tema"); return; }
    setGenerating(true); setResult(null);
    try {
      const r = await api.post("/ai/social-post", form);
      setResult(r.data);
      toast.success("Publicación generada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando");
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!result) return;
    try {
      await api.post("/social-posts", {
        caption: result.caption, hashtags: result.hashtags || [],
        image_base64: result.image_base64, platform: result.platform, topic: form.topic,
      });
      toast.success("Guardado en biblioteca");
      loadSaved();
    } catch { toast.error("Error al guardar"); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado"); };

  const downloadImage = (b64, name = "post") => {
    const a = document.createElement("a");
    a.href = b64; a.download = `${name}.png`; a.click();
  };

  const remove = async (id) => {
    await api.delete(`/social-posts/${id}`);
    setSaved(saved.filter((s) => s.id !== id));
  };

  const schedule = async (id) => {
    const dt = scheduleFor[id];
    if (!dt) { toast.error("Elige fecha y hora"); return; }
    try {
      await api.patch(`/social-posts/${id}/schedule`, { scheduled_for: new Date(dt).toISOString() });
      toast.success("Programado. Se publicará automáticamente.");
      loadSaved();
    } catch { toast.error("Error al programar"); }
  };

  const publishNow = async (id) => {
    if (!metaConfigured) { toast.error("Meta no está configurado. Configúralo en Reels."); return; }
    if (!confirm("¿Publicar ahora en Instagram + Facebook?")) return;
    try {
      toast.info("Publicando…");
      await api.post(`/meta/publish-now/social/${id}`);
      toast.success("Publicado en IG + FB");
      loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al publicar"); }
  };

  const captionWithTags = result ? `${result.caption}\n\n${(result.hashtags || []).map(h => "#" + h).join(" ")}` : "";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="social-panel">
      <div className="lg:col-span-5 bg-white rounded-2xl border border-[#E6E2DC] p-7 space-y-5">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">IA Generativa</div>
          <h3 className="font-display text-2xl text-[#1A1C1A]">Generar publicación</h3>
          <p className="text-xs text-[#5C5E5C] mt-2">Crea texto + imagen con Claude y Gemini Nano Banana.</p>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Tema o idea</Label>
          <Textarea rows={3} placeholder="Ej. Beneficios de Invisalign para adultos profesionales" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="social-topic" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Servicio</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="social-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="ortodoncia">Ortodoncia</SelectItem>
                <SelectItem value="estetica">Medicina Estética</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Plataforma</Label>
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
              <SelectTrigger data-testid="social-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Instagram">Instagram</SelectItem>
                <SelectItem value="Facebook">Facebook</SelectItem>
                <SelectItem value="TikTok">TikTok</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Tono</Label>
          <Input value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} data-testid="social-tone" />
        </div>
        {palettes.length > 0 && (
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Paleta · Mood</Label>
            <Select value={form.palette} onValueChange={(v) => setForm({ ...form, palette: v })}>
              <SelectTrigger data-testid="social-palette">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {palettes.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`social-palette-opt-${p.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="flex gap-0.5">
                        {(p.colors || []).slice(0, 4).map((c, i) => (
                          <span key={i} className="w-3 h-3 rounded-full border border-black/10" style={{ background: c }} />
                        ))}
                      </span>
                      <span className="text-sm">{p.label}</span>
                      <span className="text-[10px] text-[#A69076]">{p.tagline}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(() => {
              const p = palettes.find((x) => x.id === form.palette);
              if (!p) return null;
              return <p className="text-[11px] text-[#5C5E5C] mt-2 leading-snug">{p.description}</p>;
            })()}
          </div>
        )}
        <div className="flex items-center justify-between p-3 rounded-lg bg-[#F4F0EB]">
          <div>
            <div className="text-sm">Generar imagen con IA</div>
            <div className="text-xs text-[#5C5E5C]">Imagen editorial cuadrada con Gemini Nano Banana</div>
          </div>
          <Switch checked={form.include_image} onCheckedChange={(v) => setForm({ ...form, include_image: v })} data-testid="social-include-image" />
        </div>
        <Button onClick={generate} disabled={generating} className="w-full rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 text-xs uppercase tracking-[0.2em]" data-testid="social-generate">
          <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Generando..." : "Generar publicación"}
        </Button>
      </div>

      <div className="lg:col-span-7 space-y-6">
        {result && (
          <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7" data-testid="social-result">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Vista previa · {result.platform}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => copy(captionWithTags)} data-testid="social-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copiar</Button>
                {result.image_base64 && <Button size="sm" variant="outline" onClick={() => downloadImage(result.image_base64)} data-testid="social-download"><Download className="w-3.5 h-3.5 mr-1" /> Imagen</Button>}
                <Button size="sm" onClick={save} className="bg-[#A69076] hover:bg-[#1A1C1A] text-white" data-testid="social-save"><Save className="w-3.5 h-3.5 mr-1" /> Guardar</Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {result.image_base64 ? (
                <img src={result.image_base64} alt="Generada" className="aspect-square rounded-xl object-cover w-full" />
              ) : (
                <div className="aspect-square rounded-xl bg-[#F4F0EB] flex items-center justify-center text-[#A69076] text-xs uppercase tracking-widest">Sin imagen</div>
              )}
              <div>
                <p className="text-[15px] text-[#1A1C1A] leading-relaxed whitespace-pre-line">{result.caption}</p>
                <div className="mt-4 text-xs text-[#A69076] flex flex-wrap gap-1.5">
                  {(result.hashtags || []).map((h, i) => <span key={i}>#{h}</span>)}
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-3">Biblioteca ({saved.length})</div>
          {saved.length === 0 && <div className="bg-white rounded-2xl border border-[#E6E2DC] p-8 text-center text-sm text-[#5C5E5C]" data-testid="social-library-empty">Las publicaciones guardadas aparecerán aquí.</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {saved.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-[#E6E2DC] overflow-hidden" data-testid={`social-saved-${s.id}`}>
                {s.image_base64 && <img src={s.image_base64} alt="" className="aspect-square object-cover w-full" />}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">{s.platform}</div>
                    <div className="flex items-center gap-1">
                      {s.status === "publicado" && <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#1A1C1A] text-white">PUBLICADO</span>}
                      {s.status === "programado" && <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#D9CDBC] text-[#1A1C1A]">PROGRAMADO</span>}
                      {s.status === "publish_failed" && <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded-full bg-[#F5E0E0] text-[#965D5D]">ERROR</span>}
                      <button onClick={() => remove(s.id)} className="p-1.5 text-[#965D5D] hover:bg-[#F5E0E0] rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <p className="text-xs text-[#1A1C1A] line-clamp-4 leading-relaxed">{s.caption}</p>
                  {s.scheduled_for && (
                    <div className="text-[10px] text-[#5C5E5C] mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(s.scheduled_for).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</div>
                  )}
                  {s.last_error && <p className="text-[10px] text-[#965D5D] mt-1">⚠ {s.last_error}</p>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => copy(`${s.caption}\n\n${(s.hashtags || []).map(h => "#" + h).join(" ")}`)}>
                      <Copy className="w-3 h-3 mr-1" /> Copiar
                    </Button>
                    {s.status !== "publicado" && s.image_base64 && (
                      <Button size="sm" className="bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs" onClick={() => publishNow(s.id)} data-testid={`social-publish-${s.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Publicar IG+FB
                      </Button>
                    )}
                  </div>
                  {s.status !== "publicado" && (
                    <div className="flex gap-1.5 mt-2">
                      <Input type="datetime-local" min={new Date().toISOString().slice(0,16)} className="h-8 text-xs flex-1" value={scheduleFor[s.id] || ""} onChange={(e) => setScheduleFor({ ...scheduleFor, [s.id]: e.target.value })} data-testid={`social-schedule-input-${s.id}`} />
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => schedule(s.id)} data-testid={`social-schedule-${s.id}`}>
                        <Calendar className="w-3 h-3 mr-1" /> Programar
                      </Button>
                    </div>
                  )}
                  {s.status === "publicado" && s.meta_result && (
                    <div className="text-[10px] text-[#3a5a44] flex items-center gap-1 mt-2">
                      <CheckCircle2 className="w-3 h-3" /> Publicado en IG + FB
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
